import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/modal';
import Button from '../ui/button/Button';
import { FileItem } from '../../services/FilesService';
import { settingsService } from '../../services/SettingsService';

// Maximum character limit for the reason field
const MAX_REASON_LENGTH = 2000;

interface ReportDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (reason: string, includePdf: boolean) => void;
    fileItem: FileItem | null;
}

const ReportDialog: React.FC<ReportDialogProps> = ({ isOpen, onClose, onSubmit, fileItem }) => {
    const [reason, setReason] = useState('');
    const [includePdf, setIncludePdf] = useState(false);

    // Initialize form values when dialog opens
    useEffect(() => {
        if (isOpen) {
            setReason('');
            // Get user setting and set checkbox accordingly
            const settings = settingsService.getSettings();
            setIncludePdf(settings.defaultIncludePdfOnReport);
        }
    }, [isOpen]);

    const handleSubmit = () => {
        if (reason.trim()) {
            onSubmit(reason, includePdf);
            // The form will be reset next time the dialog opens via the useEffect
        }
    };

    const handleClose = () => {
        onClose();
        // We don't need to reset the form here as it will be reset when reopened
    };

    const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        // Enforce character limit
        if (value.length <= MAX_REASON_LENGTH) {
            setReason(value);
        }
    };

    // Calculate remaining characters
    const remainingChars = MAX_REASON_LENGTH - reason.length;
    // Determine text color for the counter based on remaining characters
    const counterColor = remainingChars < 100 ? 'text-orange-500' : 'text-gray-500';

    return (
        <Modal isOpen={isOpen} onClose={handleClose} className="max-w-lg p-6">
            <div className="text-center">
                <div className="flex justify-center mb-4">
                    <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-yellow-100 dark:bg-yellow-900/20">
                        <svg 
                            width="24" 
                            height="24" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path 
                                d="M5 21V3.90002C5 3.90002 5.875 3 8.5 3C11.125 3 12.875 4.8 15.5 4.8C18.125 4.8 19 3.9 19 3.9V14.7C19 14.7 18.125 15.6 15.5 15.6C12.875 15.6 11.125 13.8 8.5 13.8C5.875 13.8 5 14.7 5 14.7" 
                                stroke="#FF8C00" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                            />
                        </svg>
                    </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Report Conversion Issue
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Report a problem with this file to our development team.
                </p>
                
                <div className="mb-2">
                    <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                        rows={4}
                        placeholder="Describe the issue you're experiencing with this file..."
                        value={reason}
                        onChange={handleReasonChange}
                        maxLength={MAX_REASON_LENGTH}
                    />
                </div>
                
                <div className="text-right mb-4">
                    <span className={`text-xs ${counterColor}`}>
                        {remainingChars} characters remaining
                    </span>
                </div>
                
                <div className="flex items-center mb-6 justify-center">
                    <input
                        type="checkbox"
                        id="includePdf"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        checked={includePdf}
                        onChange={(e) => setIncludePdf(e.target.checked)}
                    />
                    <label htmlFor="includePdf" className="ml-2 block text-sm text-gray-600 dark:text-gray-400">
                        Include original PDF in report (may increase upload time)
                    </label>
                </div>
                
                <div className="flex flex-col sm:flex-row justify-center gap-3">
                    <Button 
                        variant="outline" 
                        onClick={handleClose}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSubmit}
                        disabled={!reason.trim()}
                        className="bg-orange-500 hover:bg-orange-600 dark:bg-orange-600 dark:hover:bg-orange-700 disabled:bg-orange-300 disabled:hover:bg-orange-300 dark:disabled:bg-orange-800/40 dark:disabled:hover:bg-orange-800/40"
                    >
                        Submit Report
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default ReportDialog; 